/**
 * Update store - Manages app version checking and PWA updates.
 *
 * Handles:
 * - Version checking against backend (feeds Settings → About, and keeps the
 *   browser downloading the new worker so the NEXT LAUNCH picks it up)
 * - Force update enforcement (the only modal left)
 * - The manual "Update Now" in Settings
 *
 * There is deliberately NO update banner (Nation, 2026-08-07: "this is getting
 * really annoying… remove it and nuke it"). Updates install quietly and take
 * over on the next clean launch — no skipWaiting, no prompt.
 */
import { create } from 'zustand';
import { api } from '../api/client';
import { isBusy, onBusyChange } from '../utils/updateSafety';

// Get build-time injected version
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
const BUILD_TIME = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : new Date().toISOString();

interface VersionCheckResponse {
  success: boolean;
  update_available: boolean;
  update_required: boolean;
  current_version: string;
  latest_version: string;
  minimum_version: string;
  message: string | null;
}

interface UpdateState {
  // Version info
  appVersion: string;
  buildTime: string;
  latestVersion: string | null;
  minimumVersion: string | null;

  // Update status
  updateAvailable: boolean;
  updateRequired: boolean;  // Force update
  updateMessage: string | null;

  // Service worker status
  swRegistration: ServiceWorkerRegistration | null;

  // UI state
  showForceUpdateModal: boolean;
  showUpdatingOverlay: boolean;  // Shows "Updating..." while reloading
  /** User tapped Update while work was in flight — applies once it drains. */
  pendingApply: boolean;
  isChecking: boolean;
  lastChecked: string | null;
  error: string | null;

  // Actions
  checkForUpdates: () => Promise<void>;
  applyUpdate: () => Promise<void>;
  setSwRegistration: (reg: ServiceWorkerRegistration) => void;
}

// Check interval in milliseconds (5 minutes)
const CHECK_INTERVAL = 5 * 60 * 1000;

/**
 * Reload latch — BUG-018. Two paths reload on update: applyUpdateNow's fallback
 * timer and the `controllerchange` listener that fires once the waiting worker
 * takes over. Whoever gets here first wins; the other returns. Without this the
 * app visibly loads twice on every update.
 */
let reloading = false;
function reloadOnce() {
  if (reloading) return;
  reloading = true;
  window.location.reload();
}

/**
 * BUG-056. Only WE are allowed to reload the page. A worker can activate on its
 * own (e.g. the last window closed and reopened), and the old code reloaded on
 * any `controllerchange` — which is how an update landed on top of an unsent
 * capture. Set only by applyUpdateNow.
 */
let applyingUpdate = false;

/**
 * Apply the waiting update: hand control to the new worker and reload ONCE.
 *
 * BUG-057: no cache deletion, no unregistering. Workbox revisions the precache
 * and `cleanupOutdatedCaches()` prunes on activate, so the new bundle is fully
 * on disk BEFORE it activates — the post-update load works with zero signal.
 * The old code deleted every cache and unregistered the worker first, which is
 * why an update on one bar left Nation staring at a blank screen.
 */
async function applyUpdateNow(
  set: (partial: Partial<UpdateState>) => void,
  get: () => UpdateState
): Promise<void> {
  set({ showUpdatingOverlay: true, pendingApply: false });
  applyingUpdate = true;

  // Brief delay so the overlay actually renders
  await new Promise((resolve) => setTimeout(resolve, 300));

  let waiting: ServiceWorker | null | undefined;
  try {
    waiting = get().swRegistration?.waiting;
  } catch {
    waiting = null;
  }
  if (waiting) {
    // The worker takes over → controllerchange → reloadOnce. The timer is the
    // backstop for a worker that never activates (never leave the overlay up).
    try {
      waiting.postMessage({ type: 'SKIP_WAITING' });
    } catch {
      reloadOnce();
      return;
    }
    setTimeout(reloadOnce, 3000);
  } else {
    // Backend says there's a new version but no worker is waiting yet — a plain
    // reload still fetches the new index.html (navigation is NetworkFirst).
    reloadOnce();
  }
}

export const useUpdateStore = create<UpdateState>()((set, get) => ({
  // Initial state
  appVersion: APP_VERSION,
  buildTime: BUILD_TIME,
  latestVersion: null,
  minimumVersion: null,

  updateAvailable: false,
  updateRequired: false,
  updateMessage: null,

  swRegistration: null,

  showForceUpdateModal: false,
  showUpdatingOverlay: false,
  pendingApply: false,
  isChecking: false,
  lastChecked: null,
  error: null,

  /**
   * Check backend for available updates.
   */
  checkForUpdates: async () => {
    const { isChecking, appVersion } = get();
    if (isChecking) return;

    set({ isChecking: true, error: null });

    try {
      const response = await api.post<VersionCheckResponse>('/version/check', {
        client_version: appVersion,
        platform: 'web',
      });

      // Response is direct (not wrapped in data property)
      // Backend returns: { success, update_available, latest_version, ... }
      const data = response as unknown as VersionCheckResponse;

      if (data.success && data.latest_version) {
        set({
          latestVersion: data.latest_version,
          minimumVersion: data.minimum_version,
          updateAvailable: data.update_available,
          updateRequired: data.update_required,
          updateMessage: data.message,
          lastChecked: new Date().toISOString(),
          showForceUpdateModal: data.update_required,
        });

        // The backend knowing about a version doesn't download it — ask the
        // browser to fetch the new worker so one is actually WAITING when the
        // user taps Update (or when they next relaunch).
        if (data.update_available) {
          try {
            await get().swRegistration?.update();
          } catch {
            // offline / no registration — the banner is still correct
          }
        }
      }
    } catch {
      // Silently fail - version check is not critical
      set({ error: 'Failed to check for updates' });
    } finally {
      set({ isChecking: false });
    }
  },

  /**
   * Apply the update. If a send or save is on the wire, or a capture is still
   * unsent, we wait for it — initUpdateChecker applies it the moment the work
   * drains (BUG-056).
   */
  applyUpdate: async () => {
    if (isBusy()) {
      set({ pendingApply: true });
      return;
    }
    await applyUpdateNow(set, get);
  },

  /**
   * Set the service worker registration.
   */
  setSwRegistration: (reg: ServiceWorkerRegistration) => {
    set({ swRegistration: reg });
  },

}));


/**
 * Initialize update checking.
 * Call this once when the app starts.
 */
export function initUpdateChecker() {
  const store = useUpdateStore.getState();

  // Check immediately on app start
  store.checkForUpdates();

  // Set up periodic checking
  setInterval(() => {
    store.checkForUpdates();
  }, CHECK_INTERVAL);

  // The registration is kept for two jobs only: checkForUpdates() nudges it to
  // DOWNLOAD a new worker (so the next launch has one waiting), and Settings'
  // Update Now hands control to that waiting worker. Nothing announces it.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      store.setSwRegistration(registration);
    });

    // Listen for controller change (new SW took over)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Reload ONLY when we asked for it (BUG-056). A worker can activate on
      // its own; reloading then is what landed an update on top of unsent work.
      if (applyingUpdate) reloadOnce();
    });
  }

  // A deferred update (tapped while sending) applies as soon as the work drains
  onBusyChange(() => {
    const current = useUpdateStore.getState();
    if (current.pendingApply && !isBusy()) void current.applyUpdate();
  });

  // Check again when the app comes back to the foreground, if it's been a while.
  // Nothing auto-applies here any more (BUG-056) — a waiting update installs on
  // the next launch, or when the user taps Update.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;

    const currentStore = useUpdateStore.getState();
    const lastChecked = currentStore.lastChecked;
    if (lastChecked) {
      const timeSinceCheck = Date.now() - new Date(lastChecked).getTime();
      if (timeSinceCheck > CHECK_INTERVAL / 2) {
        currentStore.checkForUpdates();
      }
    }
  });
}


/**
 * Get human-readable version info.
 */
export function getVersionInfo() {
  const { appVersion, buildTime, latestVersion } = useUpdateStore.getState();
  return {
    version: appVersion,
    buildTime: new Date(buildTime).toLocaleString(),
    isLatest: !latestVersion || appVersion === latestVersion,
  };
}
